import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { isApiErrorMessage } from 'src/api/utils/is-api-error-message';
import { updateAdminPhoneNumber } from 'src/features/administrators/tenant/api/lib/admins';
import { adminsKeys } from 'src/features/administrators/tenant/api/query-keys/admins';
import { ApiError } from 'src/types/interfaces/api-error';
import { delayedCallbackExecution } from 'src/utils/delayed-callback-execution';
import { ApiErrorMessage } from '../../types/api-error-message';
import { adminsApiErrorsTranslations } from '../../api/constants/api-errors-translations';

export const useUpdateAdminPhoneNumber = (onSuccess?: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      token,
      phoneNumber,
    }: {
      token: string;
      phoneNumber: string;
    }) => updateAdminPhoneNumber(token, phoneNumber),
    onSuccess: () => {
      delayedCallbackExecution(1000, () => {
        queryClient.invalidateQueries({ queryKey: adminsKeys.all });
      });
      onSuccess?.();
      toast.success(t('organizationDetails:admins.toast.success.update'));
    },
    onError: (error: ApiError) => {
      const errorMessage = error?.response?.data?.message;

      if (
        isApiErrorMessage<ApiErrorMessage>(
          errorMessage,
          adminsApiErrorsTranslations,
        )
      ) {
        toast.error(t(adminsApiErrorsTranslations[errorMessage]));

        return;
      }

      toast.error(
        error?.response?.data?.message ||
          t('common:errorMessages.somethingWentWrong'),
      );
    },
  });
};
